import React from 'react';

import { css } from '@emotion/core';
import dynamic from 'next/dynamic';

import { BREAKPOINTS, unit } from '@zego/theme';
import { TrackingScope } from '@zego/tracking';

import Link from '@zego/components/Link';

import {
  Blockquote,
  Header,
  IFrameWrapper,
  Image,
  ListItem,
  OrderedList,
  Paragraph,
  UnorderedList,
} from './blocks';

const ComboForm = dynamic(import('@zego/components/quickEstimates/ComboForm'));

const addBreaklines = children => children.map(child => [child, <br />]);

const WidgetWrapper = ({ title, bgColor, fgColor, children }) => (
  <figure
    css={css`
      clear: both;
      background-color: ${bgColor};
      padding: ${unit(6)}px 30px;
      width: 100vw;
      position: relative;
      left: 50%;
      right: 50%;
      margin-top: ${unit(10)}px;
      margin-left: -50vw;
      margin-right: -50vw;
      margin-bottom: ${unit(16)}px;
    `}
  >
    {title && (
      <figcaption>
        <Header
          css={css`
            color: ${fgColor};
            text-align: center;
            margin: 0 auto ${unit(3)}px;
            font-weight: bold;
            font-size: 24px;
            line-height: 24px;
            ${BREAKPOINTS.medium} {
              font-size: 36px;
              line-height: 36px;
            }
          `}
        >
          {title}
        </Header>
      </figcaption>
    )}
    <TrackingScope locationName="Blog Post">{children}</TrackingScope>
  </figure>
);

const renderers = {
  inline: {
    // The key passed here is just an index based on rendering order inside a block
    BOLD: (children, { key }) => <strong key={key}>{children}</strong>,
    ITALIC: (children, { key }) => <em key={key}>{children}</em>,
    UNDERLINE: (children, { key }) => <u key={key}>{children}</u>,
  },

  blocks: {
    unstyled: children =>
      children.map(child => <Paragraph>{child}</Paragraph>),
    blockquote: children => <Blockquote>{addBreaklines(children)}</Blockquote>,
    'header-two': children => children.map(child => <Header>{child}</Header>),
    'unordered-list-item': (children, { keys }) => (
      <UnorderedList key={keys[keys.length - 1]}>
        {children.map(child => (
          <ListItem>{child}</ListItem>
        ))}
      </UnorderedList>
    ),
    'ordered-list-item': (children, { keys }) => (
      <OrderedList key={keys.join('|')}>
        {children.map((child, index) => (
          <ListItem key={keys[index]}>{child}</ListItem>
        ))}
      </OrderedList>
    ),
    atomic: (children, { keys, data }) =>
      children.map((child, i) => {
        switch (data[i].type) {
          case 'youtube':
            return (
              <IFrameWrapper
                title={data[i].videoID}
                videoID={data[i].videoID}
              />
            );
          case 'image':
            const { width, height, ...props } = data[i];
            return <Image key={keys[i]} ratio={width / height} {...props} />;
          case 'widget':
            switch (data[i].display) {
              case 'combined-quick-estimates':
                return (
                  <WidgetWrapper {...data[i]}>
                    <ComboForm verticals={data[i]['verticals']} />
                  </WidgetWrapper>
                );
              case 'scooter-quick-estimates':
                return (
                  <WidgetWrapper {...data[i]}>
                    <ComboForm verticals={['scooter']} />
                  </WidgetWrapper>
                );
              case 'van-quick-estimates':
                return (
                  <WidgetWrapper {...data[i]}>
                    <ComboForm verticals={['van']} />
                  </WidgetWrapper>
                );
              case 'private-hire-quick-estimates':
                return (
                  <WidgetWrapper {...data[i]}>
                    <ComboForm verticals={['private-hire']} />
                  </WidgetWrapper>
                );
              case 'car-quick-estimates':
                return (
                  <WidgetWrapper {...data[i]}>
                    <ComboForm verticals={['car']} />
                  </WidgetWrapper>
                );
              default:
                return null;
            }

          default:
            return null;
        }
      }),
  },
  entities: {
    LINK: (children, data, { key }) => (
      <Link
        key={key}
        href={data.url}
        css={css`
          text-decoration: underline;
        `}
      >
        {children}
      </Link>
    ),
  },
};

export default renderers;
